import * as R from 'ramda';

const addParentId = (id, ids) => {
  const last = R.last(ids);
  return R.append(R.isNil(last) ? id : `${last}.${id}`, ids);
};

const _refineUnconfirmedPaths = paths => R.map(p => [R.head(p)], paths);

const getHierId = v => (R.isNil(v.parent) || R.isEmpty(v.parent) ? v.id : `${v.parent}.${v.id}`);

export const applyHierarchicalCodesToDim = (hCodes, dim) => {
  const confirmedHierarchisedChilds = new Set([]);
  let unconfirmedPaths = {};

  const indexedValues = R.reduce(
    (acc, val) => {
      if (!val.isSelected) {
        return acc;
      }
      return R.assoc(val.id, val, acc);
    },
    {},
    dim.values || [],
  );

  const parseHCodes = (parentsDef, parentsInDim) =>
    R.reduce((acc, hC) => {
      const { codeID, hierarchicalCodes = [] } = hC;
      const hasInDim = R.has(codeID, indexedValues);
      const isUnconfirmed = R.length(parentsInDim) !== R.length(parentsDef);
      const children = parseHCodes(
        R.append(codeID, parentsDef),
        hasInDim ? addParentId(codeID, parentsInDim) : parentsInDim,
      )(hierarchicalCodes);
      if (!hasInDim) {
        return R.concat(acc, children);
      }
      const path = R.join('.', parentsDef);
      let val = {
        ...R.prop(codeID, indexedValues),
        path,
        parents: parentsInDim,
        parent: R.last(parentsInDim),
        children,
      };

      if (isUnconfirmed) {
        unconfirmedPaths = R.over(
          R.lensProp(val.id),
          paths => R.append(path, paths || []),
          unconfirmedPaths,
        );
        val = R.assoc('unconfirmed', true, val);
        return R.append(val, acc);
      }
      confirmedHierarchisedChilds.add(codeID);
      return R.append(val, acc);
    }, []);

  const parsed = parseHCodes([], [])(hCodes);

  const _unconfirmedPaths = R.pipe(
    R.reject(id => confirmedHierarchisedChilds.has(id)),
    ids => R.pick(ids, unconfirmedPaths),
  )(R.keys(unconfirmedPaths));

  const refinedUnconfirmedPaths = _refineUnconfirmedPaths(_unconfirmedPaths);

  const flattenDescendants = R.reduce((acc, val) => {
    const children = flattenDescendants(val.children || []);
    return R.concat(acc, R.prepend(val, children));
  }, []);

  const isValid = val =>
    !val.unconfirmed || val.path === R.head(refinedUnconfirmedPaths[val.id] || []);

  const refineCodes = R.reduce((acc, hC) => {
    const _flatDescendants = flattenDescendants(hC.children);
    const flatDescendants = R.prepend(hC, _flatDescendants);

    const valids = R.filter(isValid, flatDescendants);

    if (R.isEmpty(valids)) {
      return acc;
    }
    const refined = R.pipe(
      R.reduce((_acc, v) => {
        confirmedHierarchisedChilds.add(v.id);
        const prev = R.last(_acc);
        const parents = R.pipe(
          p => (R.isNil(p) ? [] : R.append(getHierId(p), prev.parents || [])),
          R.filter(id => R.includes(id, v.parents)),
        )(prev);
        const rV = {
          ...R.omit(['unconfirmed', 'children', 'path'], v),
          parents,
          parent: R.isEmpty(parents) ? undefined : R.last(parents),
        };
        return R.append(rV, _acc);
      }, []),
    )(valids);
    return R.concat(acc, refined);
  }, []);

  const refined = refineCodes(parsed);

  const missingValues = R.pipe(
    R.keys,
    R.filter(id => !confirmedHierarchisedChilds.has(id)),
    ids => R.props(ids, indexedValues),
    R.map(v => ({ ...v, parents: [], parent: undefined })),
    R.sortBy(R.propOr(-1, '__indexPosition')),
  )(indexedValues);

  let hierarchicalIndexes = {};
  return R.pipe(
    R.concat,
    R.addIndex(R.map)((v, ind) => {
      const parents = R.props(v.parents, hierarchicalIndexes);
      const hierId = getHierId(v);
      hierarchicalIndexes[hierId] = ind;
      return {
        ...R.dissoc('path', v),
        parents,
        __indexPosition: ind,
      };
    }),
    values => ({ ...dim, values }),
  )(missingValues, refined);
};
